import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { step0Schema, loginSchema } from '@shared/lib/validators/registration-schema';
import { Controller, useForm } from 'react-hook-form';
import {
  selectCredentials,
  selectCurrentStep,
  setExistingUser,
  setStep,
} from '../../../entities/registration/model';
import { useEffect, useState } from 'react';
import { Button } from '../../../shared/ui/button';
import { InputField } from '../../../shared/ui/input-field';
import { useParams } from 'react-router-dom';
import { RegisterHeader } from './RegistrationStepCompanyData';
import { CONSENT_VERSION, ConsentCheckboxes } from '@shared/ui/legal-consent';
import {
  fetchInviteById,
  selectCurrentInvite,
  selectInviteStatus,
} from '../../../entities/invite/model';
import {
  checkUsernameAvailable,
  registerEmployee,
} from '../../../entities/registration/model';
import ReactInputMask from 'react-input-mask';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { authApi } from '../../../api/services/auth.service';
import {
  setErrorNotification,
  setNotification,
} from '../../../app/store/notifications';

export const RegistrationStepCredentials = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectInviteStatus);
  const step = useAppSelector(selectCurrentStep);
  const credentialData = useAppSelector(selectCredentials);
  const invite = useAppSelector(selectCurrentInvite);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(step0Schema),
    shouldUnregister: false,
    defaultValues: {
      email: '',
      username: '',
      password: '',
      terms: false,
      consent_marketing: false,
      first_name: '',
      last_name: '',
      mobile_phone: '',
    },
  });

  const consentMarketing = watch('consent_marketing');

  const {
    register: loginRegister,
    handleSubmit: loginHandleSubmit,
    formState: { errors: loginErrors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  useEffect(() => {
    reset(credentialData);
  }, [credentialData, reset]);

  useEffect(() => {
    // ВАЖНО: стреляем ТОЛЬКО при `idle` — то есть один раз за
    // монтирование. Раньше тут было `status !== 'succeeded'`, и
    // любой не-успешный ответ (loading/failed) триггерил effect
    // снова: при 429 от throttle status переходил failed → effect
    // снова дёргал fetch → снова 429 → бесконечный цикл (см. сетевой
    // лог 18.05.2026, всё сотками 429). С `idle` цикла нет: если
    // первый запрос упал, retry — это уже явное действие юзера
    // (reload страницы), а не невидимый авто-loop.
    if (status === 'idle') {
      dispatch(fetchInviteById(id?.toString() || ''));
    }
  }, [dispatch, status, id]);

  const onSubmit = async (data: Record<string, unknown>) => {
    // ВАЖНО: yup-схема (step0Schema) не знает про «логин занят» —
    // это решает бэк. Если опираться только на setError из onBlur, при
    // нажатии «Продолжить» валидация yup срабатывает первой и затирает
    // ручную ошибку — красная подсветка слетала и юзеру казалось, что
    // форма «как будто принята». Поэтому проверяем доступность логина
    // прямо здесь, ПОСЛЕ успешной yup-валидации. setError в onSubmit
    // становится «последним словом» и держит подсветку до тех пор,
    // пока юзер не изменит поле.
    //
    // Локальные тосты «Логин уже занят» убраны намеренно: дальше в
    // ветке для invite.type !== 'employee' дёргается thunk
    // `checkUsernameAvailable` (см. entities/registration/model), у
    // которого свой собственный setErrorNotification. Из-за этого
    // юзер видел два одинаковых тоста подряд. Здесь только подсветка
    // и фокус, тост (если дойдёт до thunk) скажет сам бэк.
    const username = String(data.username || '').trim();
    if (username) {
      try {
        const response = await authApi.checkUsernameAvailable(username);
        if (!response.data) {
          setError(
            'username',
            {
              type: 'manual',
              message: 'Логин уже занят. Выберите другой.',
            },
            { shouldFocus: true }
          );
          return;
        }
      } catch (error) {
        dispatch(setErrorNotification('Не удалось проверить логин.'));
        return;
      }
    }

    if (invite.type === 'employee') {
      dispatch(
        registerEmployee({
          ...data,
          invite: invite.id,
          // Фиксация согласий на бэке (legal): отметка ПДн и рассылок + версия.
          consent_personal_data: Boolean(data.terms),
          consent_marketing: Boolean(data.consent_marketing),
          consent_version: CONSENT_VERSION,
        })
      );
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(checkUsernameAvailable({ ...data, ...invite } as any));
  };

  const handleUsernameBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    // Мгновенная обратная связь: ставим только подсветку поля, без
    // тоста — иначе юзер при сабмите видел бы дубль (одно уведомление
    // от blur, другое от onSubmit/thunk). «Громкий» тост оставлен на
    // долю серверной ошибки.
    const username = event.target.value;
    if (username) {
      authApi.checkUsernameAvailable(username).then((response) => {
        if (response.data) {
          dispatch(setNotification('Логин не занят!'));
        } else if (!response.data) {
          setError('username', {
            type: 'manual',
            message: 'Логин занят!',
          });
        }
      });
    }
  };

  const onLoginSubmit = async (data: any) => {
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const response = await authApi.login(data.username, data.password);
      const token = response.data.token;
      const user = response.data.user;

      // (Гостевые инвайты выпилены — соответствующая ветка use_guest_invite
      // удалена. Все приглашения ведут через стандартный регистрационный
      // флоу или employee-инвайт.)

      dispatch(
        setExistingUser({
          token,
          user: {
            username: user.username,
            email: user.email,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            mobile_phone: user.mobile_phone || '',
            password: '',
            terms: true,
          },
        })
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || 'Ошибка входа. Проверьте логин и пароль.';
      setLoginError(errorMessage);
      dispatch(setErrorNotification(errorMessage));
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (showLoginForm) {
    return (
      <>
        <RegisterHeader step={step} title="Вход в существующий аккаунт" />
        <div style={{ marginBottom: '15px', textAlign: 'center' }}>
          <span style={{ color: '#626262', fontSize: '16px' }}>Нет аккаунта? </span>
          <span
            onClick={() => { setShowLoginForm(false); setLoginError(''); }}
            style={{ cursor: 'pointer', color: '#E3282A', fontWeight: 600 }}
          >
            Зарегистрироваться
          </span>
        </div>
        <form className="w-full" onSubmit={loginHandleSubmit(onLoginSubmit)}>
          <InputField inputName="username" id="login-username" label="Email или Логин" placeholder="Введите Email или Логин" register={loginRegister} errors={loginErrors} />
          <InputField inputName="password" type="password" id="login-password" label="Пароль" placeholder="Введите пароль" register={loginRegister} errors={loginErrors} />
          {loginError && (
            <p style={{ color: '#E3282A', fontSize: '14px', marginBottom: '10px' }}>{loginError}</p>
          )}
          <Button type="submit" status={isLoggingIn ? 'loading' : 'idle'}>Войти</Button>
        </form>
      </>
    );
  }

  return (
    <>
      <RegisterHeader step={step} title="Регистрационные данные" />
      <div style={{ marginBottom: '15px', textAlign: 'center' }}>
        <span style={{ color: '#626262', fontSize: '16px' }}>У вас уже есть аккаунт? </span>
        <span
          onClick={() => setShowLoginForm(true)}
          style={{ cursor: 'pointer', color: '#E3282A', fontWeight: 600 }}
        >
          Войти
        </span>
      </div>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <InputField inputName="first_name" id="first_name" label="Имя" register={register} errors={errors} />
        <InputField inputName="last_name" id="last_name" label="Фамилия" register={register} errors={errors} />
        <InputField inputName="username" id="username" label="Придумайте логин" register={register} errors={errors} onBlur={handleUsernameBlur} />
        <InputField inputName="email" id="email" label="Email" register={register} errors={errors} />
        <InputField inputName="password" type="password" id="password" label="Придумайте пароль" register={register} errors={errors} />
        <InputField
          as={ReactInputMask}
          mask={'+7 (9**) ***-**-**'}
          id="mobile_phone"
          inputName="mobile_phone"
          label="Телефон"
          errors={errors}
          register={register}
        />
        {/* Согласия — общий компонент (shared/ui/legal-consent): обязательное
            на обработку ПДн + необязательное на рассылки, тексты в модалке. */}
        <Controller
          name="terms"
          control={control}
          render={({ field }) => (
            <ConsentCheckboxes
              className="my-5"
              personalData={Boolean(field.value)}
              onPersonalDataChange={(v) => field.onChange(v)}
              marketing={Boolean(consentMarketing)}
              onMarketingChange={(v) => setValue('consent_marketing', v)}
              error={errors.terms?.message as string | undefined}
            />
          )}
        />
        {/* Без обязательного согласия кнопка неактивна (требование к форме). */}
        <div className="flex justify-end">
          <Button type="submit" status={status} disabled={!watch('terms')} className="!w-[189px]">
            Продолжить
          </Button>
        </div>
      </form>
    </>
  );
};
